import React, {
	CSSProperties,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react'
import { TbWindowMaximize, TbX } from 'react-icons/tb'
import { useDispatch } from 'react-redux'

import { Portal } from '@/components'
import { useAppStore, useWindowEvent } from '@/hooks'
import {
	isEditableElement,
	isTextEditingKey,
} from '@/hooks/useKeyboardNavigation'
import { saveDialogSize } from '@/store/modules/user/actions'
import mainColors from '@/styles/mainColors'
import { ModalProps } from '@/types'

import {
	Body,
	Dialog,
	Footer,
	Header,
	Maximizer,
	Popup,
	PopupBackground,
	Resizer,
} from './styles'
import { ResizeData } from './types'
import { getResizedData, getResizedStyle } from './utils'

const RELATIVE_MAX_WIDTH = 0.98
const RELATIVE_MAX_HEIGHT = 0.98

export const Modal = ({
	children,
	contentStyle,
	height,
	header,
	footer,
	open,
	onClose,
	disablePortal,
	headerStyle,
	footerStyle,
	bodyStyle,
	stretchFooterButtons = true,
	closeButtonVisible,
	closeOnBackdropClick = false,
	closeOnEscape = true,
	maximizeButtonVisible,
	resizable,
	dialogId,
	zIndex,
	maximized,
	stickyFooter,
}: ModalProps) => {
	const stopEvent = useCallback((e: React.MouseEvent) => {
		e.stopPropagation()
		e.nativeEvent.stopImmediatePropagation()
	}, [])

	// Prevent keyboard navigation keys from leaking to parent components (e.g., MRT table)
	const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
		if (isEditableElement(e.target) && isTextEditingKey(e.key)) {
			e.stopPropagation()
		}
	}, [])

	const refMaximized = useRef(false)
	const [isResizing, setIsResizing] = useState(false)

	const [resizedData, setResizedData] = useState<ResizeData>({
		height:
			typeof contentStyle?.height === 'number'
				? contentStyle.height
				: undefined,
		width:
			typeof contentStyle?.width === 'number' ? contentStyle.width : undefined,
		isMaximized: !!maximized,
	})

	const dialog = useAppStore((state) => state.user.dialog.dimensions)
	const dispatch = useDispatch()

	useEffect(() => {
		if (dialogId) {
			const dialogSize = dialog[dialogId]

			if (dialogSize) {
				setResizedData({
					width: dialogSize.width,
					height: dialogSize.height,
					isMaximized:
						dialogSize.isMaximized ||
						(window.innerWidth * RELATIVE_MAX_WIDTH < dialogSize.width &&
							window.innerHeight * RELATIVE_MAX_HEIGHT < dialogSize.height),
				})
			}
		}
	}, [])

	useWindowEvent('keyup', (e: KeyboardEvent) => {
		if (closeOnEscape && open && onClose && e.key === 'Escape') {
			onClose()
		}
	})

	// F11 to toggle fullscreen/maximize
	useWindowEvent('keydown', (e: KeyboardEvent) => {
		if (open && maximizeButtonVisible && e.key === 'F11') {
			e.preventDefault()
			onMaximizeClick()
		}
	})

	useWindowEvent('mouseup', () => {
		if (isResizing) {
			setIsResizing(false)
			trySaveCurrentDialogData(resizedData)
		}
	})

	const trySaveCurrentDialogData = useCallback(
		(data: ResizeData) => {
			if (dialogId && data.width && data.height) {
				dispatch(
					saveDialogSize(
						dialogId,
						data.width <= 0 ? 1 : data.width,
						data.height <= 0 ? 1 : data.height,
						data.isMaximized,
					),
				)
			}
		},
		[dialogId, dispatch],
	)

	useWindowEvent('mousemove', (e: MouseEvent) => {
		if (isResizing) {
			e.preventDefault()
			setResizedData(getResizedData(e, RELATIVE_MAX_WIDTH, RELATIVE_MAX_HEIGHT))
		}
	})

	const onResizerMouseDown = () => {
		setIsResizing(true)
	}

	const onMaximizeClick = useCallback(() => {
		if (!maximizeButtonVisible) {
			return
		}

		const data = { ...resizedData, isMaximized: !resizedData.isMaximized }

		if (
			!data.isMaximized &&
			data.width &&
			data.height &&
			window.innerWidth * RELATIVE_MAX_WIDTH < data.width &&
			window.innerHeight * RELATIVE_MAX_HEIGHT < data.height
		) {
			data.width = window.innerWidth / 1.5
			data.height = window.innerHeight / 1.75
		}

		setResizedData(data)
		trySaveCurrentDialogData(data)
	}, [maximizeButtonVisible, resizedData, trySaveCurrentDialogData])

	const getPopupStyle = () => {
		if (resizable) {
			const resizedStyle: CSSProperties = getResizedStyle(
				resizedData,
				isResizing,
				RELATIVE_MAX_WIDTH,
				RELATIVE_MAX_HEIGHT,
			)

			return { minWidth: 400, minHeight: 400, ...contentStyle, ...resizedStyle }
		} else {
			return contentStyle
		}
	}

	useEffect(() => {
		if (maximized && !refMaximized.current) {
			refMaximized.current = true
		}
	}, [maximized])

	const handleBackdropClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (e.target !== e.currentTarget) {
				return
			}

			if (closeOnBackdropClick && onClose) {
				onClose()
			}
		},
		[closeOnBackdropClick, onClose],
	)

	const isResizedColor = resizedData?.isMaximized
		? mainColors.highlight
		: mainColors.primary

	const popup = (
		<>
			{open && (
				<PopupBackground
					zIndex={zIndex}
					data-testid="modal-background"
					onClick={handleBackdropClick}
				>
					<Popup
						style={getPopupStyle() as React.CSSProperties}
						data-testid="modal-popup"
						onClick={stopEvent}
					>
					<Dialog
						$dialogHeight={height}
						role="dialog"
						onClick={stopEvent}
						onKeyDown={handleKeyDown}
						data-testid="modal-dialog"
					>
							{(header || maximizeButtonVisible || closeButtonVisible) && (
								<Header
									style={
										{
											...headerStyle,
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
										} as React.CSSProperties
									}
									onDoubleClick={(e) => {
										if (
											!(e.target instanceof HTMLElement && e.target.closest('button'))
										) {
											onMaximizeClick()
										}
									}}
									data-testid="modal-header"
								>
									{typeof header === 'function'
										? header(onClose || (() => null))
										: header}
									<div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
										{maximizeButtonVisible && (
											<Maximizer
												onClick={onMaximizeClick}
												data-testid="modal-maximize-button"
											>
												<TbWindowMaximize color={isResizedColor} />
											</Maximizer>
										)}
										{closeButtonVisible && onClose && (
											<Maximizer
												onClick={onClose}
												data-testid="modal-close-button"
											>
												<TbX color={mainColors.primary} />
											</Maximizer>
										)}
									</div>
								</Header>
							)}
							<Body
								style={bodyStyle as React.CSSProperties}
								data-testid="modal-body"
							>
								{typeof children === 'function'
									? children(onClose || (() => null))
									: children}
							</Body>
							{footer && (
								<Footer
									style={footerStyle as React.CSSProperties}
									$stretchFooterButtons={stretchFooterButtons}
									stickyFooter={stickyFooter}
									data-testid="modal-footer"
								>
									{typeof footer === 'function'
										? footer(onClose || (() => null))
										: footer}
								</Footer>
							)}
						</Dialog>
						{resizable && (
							<Resizer
								onMouseDown={onResizerMouseDown}
								data-testid="modal-resizer"
							/>
						)}
					</Popup>
				</PopupBackground>
			)}
		</>
	)

	if (disablePortal) {
		return popup
	} else {
		return <Portal>{popup}</Portal>
	}
}
